import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CaptionMD, CaptionSM, EyebrowSM, TitleMD } from '@/atoms/Text'
import { useThemeContext } from '@/contexts/ThemeContext'
import { Offer } from '@/services/PifService'
import { formatPrice } from '@/services/PriceFormatter'
import { useTranslate } from '@/utils/translate/translate-client'
import { ExpressGradientWrapper } from '@/views/ExpressCheckoutView/ExpressCheckoutComplete/components/ExpressGradientWrapper'
import { hasExclusiveScene } from '../../utils'

interface Props {
  offer: Offer
  projectSlug: string
}
export const ExpressReceipt: FC<Props> = ({ offer, projectSlug }) => {
  const { isDarkMode } = useThemeContext()
  const { t } = useTranslate('pif')
  const price = formatPrice(offer?.price, { includeDecimals: false, currency: offer?.currency })
  const perks = useMemo(() => offer?.offeredproductSet, [offer?.offeredproductSet])

  return (
    <FlexColumn className="gap-4">
      <TitleMD weight="bold" color={isDarkMode ? 'white' : 'core-gray-950'}>
        {t('yourReceipt', 'Your Receipt')}
      </TitleMD>
      <ExpressGradientWrapper>
        <FlexColumn className={classNames('gap-4 rounded-lg p-4', isDarkMode ? 'bg-core-gray-900' : 'bg-white')}>
          <EyebrowSM color={isDarkMode ? 'white' : 'core-gray-950'} className="uppercase">
            {t('orderItems', 'Order Items')}
          </EyebrowSM>
          {perks?.map((perk) => {
            if (!perk) return null

            return (
              <CaptionMD italic color={isDarkMode ? 'white' : 'core-gray-950'} key={perk.id}>
                {perk.name}
              </CaptionMD>
            )
          })}
          {hasExclusiveScene(projectSlug) && (
            <CaptionMD italic color={isDarkMode ? 'white' : 'core-gray-950'}>
              {t('exclusiveNewScenePerkExpressV2', 'Exclusive new scene delivered by email.')}
            </CaptionMD>
          )}
          <div className={classNames('w-full h-[1px] opacity-20', isDarkMode ? 'bg-white' : 'core-gray-950')} />
          <FlexRow className="justify-between">
            <CaptionMD weight="bold" color={isDarkMode ? 'white' : 'core-gray-950'}>
              {t('total', 'Total')}
            </CaptionMD>
            <CaptionMD weight="bold" color={isDarkMode ? 'white' : 'core-gray-950'}>
              {price}
            </CaptionMD>
          </FlexRow>
        </FlexColumn>
      </ExpressGradientWrapper>
      <CaptionSM className="ml-[1px]" italic color={isDarkMode ? 'core-gray-500' : 'core-gray-600'}>
        {t('aReceiptWillBeSentToYourEmail', 'A receipt will be sent to your email.')}
      </CaptionSM>
    </FlexColumn>
  )
}
